import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Menu, X, Bell, User, LogOut, Settings } from 'lucide-react';
import { Menu as HeadlessMenu, Transition } from '@headlessui/react';
import { cn } from '@utils/cn';
import { Button } from '@components/common/Button';
import { APP_NAME, APP_DESCRIPTION, ROUTES } from '@utils/constants';
import type { RootState } from '@store/index';
import { logout } from '@store/authSlice';
import { toggleSidebar } from '@store/uiSlice';
import logoImage from '@assets/logo/logo.png';

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const { sidebarOpen } = useSelector((state: RootState) => state.ui);

  const handleLogout = () => {
    dispatch(logout());
    navigate(ROUTES.HOME);
  };

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Title */}
          <div className="flex items-center space-x-4">
            {isAuthenticated && (
              <button
                onClick={handleToggleSidebar}
                className="p-2 rounded-md text-gray-600 hover:bg-gray-100 lg:hidden"
              >
                {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            )}

            <Link to={ROUTES.HOME} className="flex items-center space-x-3">
              <img src={logoImage} alt="NCAA Logo" className="w-10 h-10 object-contain" />
              <div className="hidden md:block">
                <h1 className="text-xl font-bold text-ncaa-primary">{APP_NAME}</h1>
                <p className="text-sm text-ncaa-medium">{APP_DESCRIPTION}</p>
              </div>
            </Link>
          </div>

          {/* Navigation Links - Public */}
          {!isAuthenticated && (
            <nav className="hidden md:flex space-x-8">
              <Link
                to={ROUTES.ABOUT}
                className="text-ncaa-dark hover:text-ncaa-primary transition-colors"
              >
                About
              </Link>
              <Link
                to={ROUTES.REQUIREMENTS}
                className="text-ncaa-dark hover:text-ncaa-primary transition-colors"
              >
                Requirements
              </Link>
              <Link
                to={ROUTES.CENTERS}
                className="text-ncaa-dark hover:text-ncaa-primary transition-colors"
              >
                Capture Centers{' '}
              </Link>
              <Link
                to={ROUTES.CONTACT}
                className="text-ncaa-dark hover:text-ncaa-primary transition-colors"
              >
                Contact
              </Link>
            </nav>
          )}

          {/* Right Section */}
          <div className="flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                {/* Notifications */}
                <button className="p-2 text-gray-600 hover:text-ncaa-primary transition-colors">
                  <Bell className="h-6 w-6" />
                </button>

                {/* User Menu */}
                <HeadlessMenu as="div" className="relative">
                  <HeadlessMenu.Button className="flex items-center space-x-2 p-2 text-sm rounded-md text-gray-700 hover:bg-gray-100">
                    <div className="w-8 h-8 bg-ncaa-primary rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-white" />
                    </div>
                    <span className="hidden md:block">{user?.email}</span>
                  </HeadlessMenu.Button>

                  <Transition
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <HeadlessMenu.Items className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="py-1">
                        <HeadlessMenu.Item>
                          {({ active }) => (
                            <Link
                              to={ROUTES.PROFILE}
                              className={cn(
                                active ? 'bg-gray-100' : '',
                                'flex items-center px-4 py-2 text-sm text-gray-700'
                              )}
                            >
                              <User className="mr-3 h-4 w-4" />
                              Profile
                            </Link>
                          )}
                        </HeadlessMenu.Item>
                        <HeadlessMenu.Item>
                          {({ active }) => (
                            <button
                              className={cn(
                                active ? 'bg-gray-100' : '',
                                'flex items-center w-full px-4 py-2 text-sm text-gray-700'
                              )}
                            >
                              <Settings className="mr-3 h-4 w-4" />
                              Settings
                            </button>
                          )}
                        </HeadlessMenu.Item>
                        <div className="border-t border-gray-100" />
                        <HeadlessMenu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleLogout}
                              className={cn(
                                active ? 'bg-gray-100' : '',
                                'flex items-center w-full px-4 py-2 text-sm text-red-700'
                              )}
                            >
                              <LogOut className="mr-3 h-4 w-4" />
                              Sign out
                            </button>
                          )}
                        </HeadlessMenu.Item>
                      </div>
                    </HeadlessMenu.Items>
                  </Transition>
                </HeadlessMenu>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate(ROUTES.LOGIN)}>
                  Sign In
                </Button>
                <Button onClick={() => navigate(ROUTES.REGISTER)}>Get Started</Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
